<template>
  <div v-if="documentList === null" class="mt-6">
    <loader />
  </div>
  <panel v-else-if="documentList.length === 0" class="mt-6 pt-6">
    There are no documents available for this office.
  </panel>
  <div v-else>
    <div class="bg-white p-6 my-6 rounded-lg">
      <div class="w-full md:w-1/2 lg:w-1/3">
        <label class="block mb-2">Document</label>
        <select v-model="selectedDocument" class="w-full block border p-3 bg-white">
          <option v-for="doc in documentList" :key="doc.id" :value="doc.id">
            {{ doc.name }}
          </option>
        </select>
      </div>
    </div>

    <panel v-if="$slots.default" class="mt-6 pt-6 text-sm">
      <slot />
    </panel>
    <loader v-if="!documentSchema || !complianceInfo" />
    <panel v-else-if="documentSchema.templates.length === 0" class="mt-6 pt-6">
      This document has no templates that are compatible with your office.
    </panel>
    <div v-else class="leading-normal text-sm">
      <div class="w-full lg:w-2/3 xxl:w-1/2">
        <p v-if="isMaster && masterCopyInfo" class="mt-4 pt-6 font-bold bg-grey px-6 py-4 rounded">
          Master Copy Saved: {{ masterCopyInfo.updatedAt | date}}, {{ masterCopyInfo.updatedAt | time}}, {{ masterCopyInfo.updatedBy }}
        </p>
        <panel v-if="isMaster && (documentSchema.requires_general_compliance_info || documentSchema.requires_sales_redress_scheme || documentSchema.requires_lettings_redress_scheme || documentSchema.requires_client_money_protection_provider)" class="mt-6 pt-6">
          <h3 class="text-lg mb-2">Compliance Information</h3>
          <p>This information is required before you can generate this document.</p>

          <template v-if="documentSchema.requires_general_compliance_info">
            <div class="mt-6">
              <label for="compliance_reg_number">* Registered Company Number (Please enter "N/A" if you are a Sole Trader)</label>
              <input
                id="compliance_reg_number"
                type="text"
                class="block flex-grow w-full p-2 h-10 border text-sm bg-grey-lighter hover:border-grey focus:border-grey-dark mt-2"
                v-model="complianceInfo.compliance_reg_number" />
              <small v-if="errorBag.get('office.compliance_reg_number')" class="block text-red-dark text-medium mt-2" v-text="errorBag.get('office.compliance_reg_number')"></small>
            </div>

            <div class="mt-6">
              <label for="compliance_reg_name">* Registered Company Name</label>
              <input
                id="compliance_reg_name"
                type="text"
                class="block flex-grow w-full p-2 h-10 border text-sm bg-grey-lighter hover:border-grey focus:border-grey-dark mt-2"
                v-model="complianceInfo.compliance_reg_name" />
              <small v-if="errorBag.get('office.compliance_reg_name')" class="block text-red-dark text-medium mt-2" v-text="errorBag.get('office.compliance_reg_name')"></small>
            </div>

            <div class="mt-6">
              <label for="compliance_reg_address">* Registered Company Address</label>
              <input
                id="compliance_reg_address"
                type="text"
                class="block flex-grow w-full p-2 h-10 border text-sm bg-grey-lighter hover:border-grey focus:border-grey-dark mt-2"
                v-model="complianceInfo.compliance_reg_address" />
              <small v-if="errorBag.get('office.compliance_reg_address')" class="block text-red-dark text-medium mt-2" v-text="errorBag.get('office.compliance_reg_address')"></small>
            </div>

            <div class="mt-6">
              <label for="compliance_reg_place">Registered Company Place (e.g: England & Wales)</label>
              <input
                id="compliance_reg_place"
                type="text"
                class="block flex-grow w-full p-2 h-10 border text-sm bg-grey-lighter hover:border-grey focus:border-grey-dark mt-2"
                v-model="complianceInfo.compliance_reg_place" />
              <small v-if="errorBag.get('office.compliance_reg_place')" class="block text-red-dark text-medium mt-2" v-text="errorBag.get('office.compliance_reg_place')"></small>
            </div>

            <div class="mt-6">
              <label for="compliance_vat_number">VAT Number</label>
              <input
                id="compliance_vat_number"
                type="text"
                class="block flex-grow w-full p-2 h-10 border text-sm bg-grey-lighter hover:border-grey focus:border-grey-dark mt-2"
                v-model="complianceInfo.compliance_vat_number" />
              <small v-if="errorBag.get('office.compliance_vat_number')" class="block text-red-dark text-medium mt-2" v-text="errorBag.get('office.compliance_vat_number')"></small>
            </div>
          </template>

          <div v-if="documentSchema.requires_sales_redress_scheme" class="mt-6">
            <label for="sales_redress_scheme">* Sales Redress Scheme</label>
            <select id="sales_redress_scheme" v-model="complianceInfo.sales_redress_scheme_id" class="block flex-grow w-full p-2 border text-sm bg-grey-lighter hover:border-grey focus:border-grey-dark mt-1">
              <option :value="null">[Please Select]</option>
              <option v-for="(option, o) in salesRedressSchemes" :key="o" :value="option.id">
                {{ option.name }}
              </option>
            </select>
            <small v-if="errorBag.get('office.sales_redress_scheme_id')" class="block text-red-dark text-medium mt-2" v-text="errorBag.get('office.sales_redress_scheme_id')"></small>
          </div>

          <div v-if="documentSchema.requires_lettings_redress_scheme" class="mt-6">
            <label for="lettings_redress_scheme">* Lettings Redress Scheme</label>
            <select id="lettings_redress_scheme" v-model="complianceInfo.lettings_redress_scheme_id" class="block flex-grow w-full p-2 border text-sm bg-grey-lighter hover:border-grey focus:border-grey-dark mt-1">
              <option :value="null">[Please Select]</option>
              <option v-for="(option, o) in lettingsRedressSchemes" :key="o" :value="option.id">
                {{ option.name }}
              </option>
            </select>
            <small v-if="errorBag.get('office.lettings_redress_scheme_id')" class="block text-red-dark text-medium mt-2" v-text="errorBag.get('office.lettings_redress_scheme_id')"></small>
          </div>

          <div v-if="documentSchema.requires_client_money_protection_provider" class="mt-6">
            <label for="cmp_provider">* Client Money Protection Provider</label>
            <select id="cmp_provider" v-model="complianceInfo.client_money_protection_provider_id" class="block flex-grow w-full p-2 border text-sm bg-grey-lighter hover:border-grey focus:border-grey-dark mt-1">
              <option :value="null">[Please Select]</option>
              <option v-for="(option, o) in cmpProviders" :key="o" :value="option.id">
                {{ option.name }}
              </option>
            </select>
            <small v-if="errorBag.get('office.client_money_protection_provider_id')" class="block text-red-dark text-medium mt-2" v-text="errorBag.get('office.client_money_protection_provider_id')"></small>
          </div>
          <small class="mt-6">* Required</small>
        </panel>

        <div v-else-if="isDisabled" class="bg-red-dark text-white p-3">
          <h3 class="text-md font-semibold">Compliance Information Required</h3>
          <p>Before you can generate this document, please visit the "Template Settings" tab and provide the required compliance information.</p>
        </div>
        <panel v-if="documentSchema.customisable_author" class="mt-6 pt-6">
          <h3 class="text-lg mb-2">Agent Information</h3>
          <div  class="mt-6">
              <label for="customisable_author">* Agent</label>
              <select id="customisable_author" v-model="agent_id" class="block flex-grow w-full p-2 border text-sm bg-grey-lighter hover:border-grey focus:border-grey-dark mt-1">
                <option :value="null">Me</option>
                <option v-for="(option, o) in agents" :key="o" :value="option.id">
                  {{ option.name }}
                </option>
              </select>
              <!-- <small v-if="errorBag.get('office.customisable_author')" class="block text-red-dark text-medium mt-2" v-text="errorBag.get('office.lettings_redress_scheme_id')"></small> -->
          </div>
          <div class="mt-6">
            <label for="customisable_author">* Agent Name</label>
            <input type="text" class="block flex-grow w-full p-2 border text-sm bg-grey-lighter hover:border-grey focus:border-grey-dark" v-model="agent_name" />
          </div>
          <div class="mt-6">
            <label for="customisable_author">* Agent Position</label>
            <input type="text" class="block flex-grow w-full p-2 border text-sm bg-grey-lighter hover:border-grey focus:border-grey-dark" v-model="agent_position" />
          </div>
        </panel>
        <div v-for="(section, s) in sections" :key="s" class="mt-6">
          <div v-if="!section[0].display_inline" class="flex mx-3">
            <button v-for="(tab, t) in section" :key="tab.id" @click="selectedTabs[s] = t; $forceUpdate()" class="border border-b-0 rounded-t p-3 text-sm mr-1" :class="selectedTabs[s] !== t ? 'bg-grey-light hover:bg-grey-light border-transparent' : 'bg-white cursor-default'">
              {{ tab.name }}
            </button>
          </div>

          <panel class="pt-6">
            <h3 class="text-lg mb-3">{{ section[selectedTabs[s]].name }}</h3>
            <div class="mb-6" v-for="(g,index) in section[selectedTabs[s]].groups" :key="g.id">
              <template v-if="!g.display_in_table">
                <h4 class="text-md mb-2"><span class="mr-2 text-left" @click="toggleGroup(index)" v-if="!isMaster && g.can_collapse && !g.is_collapsed"
                  ><i :class="expandedGroups[index] ? 'fas fa-minus' : 'fas fa-plus'"></i></span>{{ g.header_label }}</h4>
                <div class="mb-6" v-for="(v, vi) in g.variables" v-if="v.type_id !== 8 && (!isMaster || !v.hide_from_master) && !(g.can_collapse && g.is_collapsed)" :key="v.id" v-show="!g.can_collapse || expandedGroups[index]">
                  <template v-if="[3, 4].includes(v.type_id)">
                    <input type="checkbox" :id="`v${v.id}`" true-value="1" false-value="0" v-model="v.default_value" :disabled="isDisabled">
                    <label :for="`v${v.id}`">{{ v.default_label }}</label>
                  </template>
                  <template v-else-if="v.type_id === 7">
                    <label>{{ v.default_label }}</label>
                    <select :id="`v${v.id}`" v-model="v.default_value" :disabled="isDisabled" class="block flex-grow w-full p-2 border text-sm bg-grey-lighter hover:border-grey focus:border-grey-dark">
                      <option v-for="(option, o) in v.config.options" :key="o" :value="option.name">
                        {{ option.name }}
                      </option>
                    </select>
                  </template>
                  <template v-else-if="v.type_id === 2">
                    <label>{{ v.default_label }}</label>
                    <textarea
                      rows="4"
                      :disabled="isDisabled"
                      class="block flex-grow w-full p-2 border text-sm bg-grey-lighter hover:border-grey focus:border-grey-dark"
                      v-model="v.default_value">
                    </textarea>
                  </template>
                  <template v-else-if="v.type_id === 9">
                    <label>{{ v.default_label }}</label>
                    <table class="w-full mt-2">
                      <thead class="bg-grey-lighter">
                        <tr>
                          <th v-for="(header, hi) in v.config.headers" :key="hi" class="text-left p-2 border border-grey">
                            {{ header }}
                          </th>
                          <th class="text-left p-2 border border-grey"></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(row, ri) in v.default_value" :key="ri">
                          <td v-for="(header, hi) in v.config.headers" :key="hi" class="text-left p-2 border border-grey">
                            <input
                              type="text"
                              v-model="v.default_value[ri][hi]"
                              class="block flex-grow w-full p-2 border text-sm bg-grey-lighter hover:border-grey focus:border-grey-dark"
                              :disabled="isDisabled"
                            />
                          </td>
                          <td class="text-left p-2 border border-grey">
                            <button
                              v-if="v.default_value.length > 1"
                              @click="removeTableRow(v, ri)"
                              type="button"
                              class="text-red-600 hover:underline"
                              :disabled="isDisabled"
                            >
                              <i class="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <div class="flex justify-end mb-2">
                      <button
                        type="button"
                        @click="addTableRow(v)"
                        class="text-blue-600 hover:underline text-sm mt-4"
                        :disabled="isDisabled"
                      >
                      <i class="fas fa-plus-square mr-1"></i>Add Row
                      </button>
                  </div>
                  </template>
                  <template v-else>
                    <label>{{ v.default_label }}</label>
                    <input
                      type="text"
                      :disabled="isDisabled"
                      class="block flex-grow w-full p-2 h-10 border text-sm bg-grey-lighter hover:border-grey focus:border-grey-dark mt-2"
                      @keydown="validateKeyPress(v, $event)"
                      v-model="v.default_value" />
                  </template>
                  <div v-if="isRequired(v) && (!isMaster || !v.hide_from_master) && (!v.default_value || ([5, 6].includes(v.type_id) && validateCurrency(v.default_label, v.default_value)))" class="text-red-dark text-xs mt-2">REQUIRED</div>
                  <small v-if="[5, 6].includes(v.type_id) && validateCurrency(v.default_label, v.default_value)" class="block text-red-dark text-medium mt-2">{{ validateCurrency(v.default_label, v.default_value) }}</small>
                  <small v-if="errorBag.get(`variables.${vi}.value`)" class="block text-red-dark text-medium mt-2" v-text="errorBag.get(`variables.${vi}.value`)"></small>
                </div>
              </template>
              <table v-else class="w-full">
                <thead class="bg-grey-lighter">
                  <tr>
                    <th class="text-left p-2 border border-grey">{{ g.header_label }}</th>
                    <th class="text-left p-2 border border-grey">{{ g.header_value }}</th>
                    <th class="text-right p-2 border border-grey" @click="toggleGroup(index)" v-if="g.can_collapse && !g.is_collapsed">
                      <i :class="expandedGroups[index] ? 'fas fa-minus' : 'fas fa-plus'"></i>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(v, vi) in g.variables" v-if="v.type_id !== 8 && (!isMaster || !v.hide_from_master) && !(g.can_collapse && g.is_collapsed)" :key="v.id" v-show="!g.can_collapse || expandedGroups[index]">
                    <td class="text-left p-2 border border-grey" :class="{'pl-12': v.indent}">
                      <input
                        v-if="v.customisable_label"
                        :disabled="isDisabled"
                        type="text"
                        class="block flex-grow w-full p-2 border text-sm bg-grey-lighter hover:border-grey focus:border-grey-dark"
                        v-model="v.default_label" />
                      <span v-else>{{ v.default_label }}</span>
                      <div v-if="missingRequiredCount && v.required && (!isMaster || !v.hide_from_master) && (!v.default_value || ([5, 6].includes(v.type_id) && validateCurrency(v.default_label, v.default_value)))" class="text-red-dark text-xs mt-2">REQUIRED</div>
                    </td>
                    <td class="text-left p-2 border border-grey w-1/3">
                      <template v-if="[3, 4].includes(v.type_id)">
                        <input type="checkbox" :id="`v${v.id}`" v-model="v.default_value" true-value="1" false-value="0" :disabled="isDisabled">
                        <label :for="`v${v.id}`">&nbsp;</label>
                      </template>
                      <template v-else-if="v.type_id === 7">
                        <select :id="`v${v.id}`" v-model="v.default_value" :disabled="isDisabled" class="block flex-grow w-full p-2 border text-sm bg-grey-lighter hover:border-grey focus:border-grey-dark">
                          <option v-for="(option, o) in v.config.options" :key="o" :value="option.name">
                            {{ option.name }}
                          </option>
                        </select>
                      </template>
                      <template v-else-if="v.type_id === 2">
                        <textarea
                          rows="4"
                          :disabled="isDisabled"
                          class="block flex-grow w-full p-2 border text-sm bg-grey-lighter hover:border-grey focus:border-grey-dark"
                          v-model="v.default_value">
                        </textarea>
                      </template>
                      <input
                        v-else
                        type="text"
                        :disabled="isDisabled"
                        class="block flex-grow w-full p-2 border text-sm bg-grey-lighter hover:border-grey focus:border-grey-dark"
                        @keydown="validateKeyPress(v, $event)"
                        v-model="v.default_value" />
                      <small v-if="[5, 6].includes(v.type_id) && validateCurrency(v.default_label, v.default_value)" class="block text-red-dark text-medium mt-2">{{ validateCurrency(v.default_label, v.default_value) }}</small>
                      <small v-if="errorBag.get(`variables.${vi}.value`)" class="block text-red-dark text-medium mt-2" v-text="errorBag.get(`variables.${vi}.value`)"></small>
                    </td>
                    <td class="text-right p-2 border border-grey w-1/3" v-if="g.can_collapse"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </panel>

          <div v-if="missingRequiredCount > 0 && !isMaster" class="bg-grey text-black p-3 mt-6">
            <h3 class="text-md font-semibold">Required Information</h3>
            <p>{{ missingRequiredCount === 1 ? 'There\'s 1 field' : `There are ${missingRequiredCount} fields` }} missing required information. You can {{ isMaster ? 'save the master copy' : 'generate this document' }} once all required fields are populated.</p>
          </div>
        </div>
      </div>

      <template v-if="!isDisabled && (missingRequiredCount === 0 || isMaster)">
        <panel class="pt-6 mt-6 w-full md:w-1/2" v-if="documentSchema.templates.length > 1">
          <label>Template</label>
          <select
            class="block flex-grow w-full p-2 border text-sm bg-grey-lighter hover:border-grey focus:border-grey-dark mt-2 mb-6"
            v-model="selectedTemplate">
            <option v-for="template in documentSchema.templates" :key="template.id" :value="template.id">
              {{ template.name }}
            </option>
          </select>

          <div class="flex flex-wrap justify-end">
            <template v-if="!isMaster">
              <button type="button" @click="generateDocument(false)" :disabled="isDisabled || generatingDocument" class="bg-white hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow self-end ml-3">
                <loader v-if="generatingDocument" />
                <span v-else><i class="fas fa-file-pdf fa-fw mr-1"></i> Generate PDF</span>
              </button>

              <button v-if="documentSchema.variable_tabs.length > 0" type="button" @click="openSaveModal()" :disabled="isDisabled" class="bg-white hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow ml-3">
                <i class="fas fa-save fa-fw mr-1"></i> Save
              </button>
            </template>
            <template v-else>
              <button v-if="documentSchema.variable_tabs.length > 0" type="button" @click="saveMasterCopy" :disabled="isDisabled || savingMaster" class="bg-white hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow ml-3">
                <loader v-if="savingMaster" />
                <span v-else><i class="fas fa-save fa-fw mr-1"></i> Save as Master Copy</span>
              </button>

              <button v-if="documentSchema.type_id !== null" type="button" @click="showSaveToWebsiteModal = true" :disabled="isDisabled" class="bg-white hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow ml-3">
                <i class="fas fa-cloud fa-fw mr-1"></i> Save To Website
              </button>
            </template>
          </div>
        </panel>
        <div v-else class="mt-6 flex flex-wrap">
          <template v-if="!isMaster">
            <button type="button" @click="generateDocument(false)" :disabled="isDisabled || generatingDocument" class="bg-white hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow self-end mr-3">
              <loader v-if="generatingDocument" />
              <span v-else><i class="fas fa-file-pdf fa-fw mr-1"></i> Generate PDF</span>
            </button>

            <button v-if="documentSchema.variable_tabs.length > 0" type="button" @click="openSaveModal()" :disabled="isDisabled" class="bg-white hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow mr-3">
              <i class="fas fa-save fa-fw mr-1"></i> Save
            </button>
          </template>
          <template v-else>
            <button v-if="documentSchema.variable_tabs.length > 0" type="button" @click="saveMasterCopy" :disabled="isDisabled || savingMaster" class="bg-white hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow mr-3">
              <loader v-if="savingMaster" />
              <span v-else><i class="fas fa-save fa-fw mr-1"></i> Save as Master Copy</span>
            </button>

            <button v-if="documentSchema.type_id !== null" type="button" @click="showSaveToWebsiteModal = true" :disabled="isDisabled" class="bg-white hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow">
              <i class="fas fa-cloud fa-fw mr-1"></i> Save To Website
            </button>
          </template>
        </div>
      </template>

      <deletion-modal
        v-if="showSaveToWebsiteModal"
        :loading="generatingDocument"
        confirm-label="Yes"
        cancel-label="Cancel"
        @confirmed="generateDocument(true)"
        @leave="showSaveToWebsiteModal = false"
        @dismissed="showSaveToWebsiteModal = false">
        <p class="text-center">Are you sure you want to upload this document to the website?</p>
      </deletion-modal>

      <modal v-if="showSaveModal" @leave="showSaveModal = false" show-close-btn>
        <h2 class="font-bold text-lg mb-4">Save Document</h2>
        <div class="mb-6">
          <label>Document Name:</label>
          <input
            type="text"
            class="block flex-grow w-full p-2 h-10 border text-sm bg-grey-lighter hover:border-grey focus:border-grey-dark mt-2"
            v-model="savedDocData.name" />
        </div>
        <div class="mb-6" v-if="savedDocId">
          <input type="radio" id="overwrite" v-model="savedDocData.save_as_id" :value="0" />
          <label for="overwrite" class="mr-12">Overwrite existing document</label>
          <input type="radio" id="new_doc" v-model="savedDocData.save_as_id" :value="1" />
          <label for="new_doc">Save as new document</label>
        </div>
        <div class="flex flex-wrap justify-end">
          <button type="button" @click="saveDocument" :disabled="saving" class="bg-white hover:bg-tertiary active:bg-primary text-primary hover:text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow">
            Save Document
          </button>
        </div>
      </modal>
    </div>
  </div>
</template>

<script>
import api, { v2Api } from '@/http'
import { mapActions, mapMutations, mapState } from 'vuex'
import ErrorBag from '@/ErrorBag'
import DeletionModal from '@/components/Modals/DeletionModal'

export default {
  components: { DeletionModal },
  props: {
    isMaster: {
      type: Boolean,
      default: false
    },
    savedDocId: {
      type: Number,
      default: null
    }
  },
  data () {
    return {
      documentList: null,
      selectedDocument: null,
      documentSchema: null,
      selectedTabs: [],
      selectedTemplate: null,
      savingMaster: false,
      saving: false,
      savedDocData: {
        name: 'New Document',
        save_as_id: 0
      },
      complianceInfo: null,
      generatingDocument: false,
      showSaveToWebsiteModal: false,
      showSaveModal: false,
      errorBag: new ErrorBag(),
      redressSchemes: [],
      cmpProviders: [],
      agents: [],
      agent_id: null,
      agent_name: '',
      agent_position: '',
      expandedGroups: {},
      masterCopyInfo: null
    }
  },
  computed: {
    ...mapState(['selectedOffice', 'profile']),
    sections () {
      let output = []
      if (!this.documentSchema) {
        return output
      }
      for (let t = 0; t < this.documentSchema.variable_tabs.length; t++) {
        if (
          output.length === 0 ||
          output[output.length - 1][0].display_inline ||
          output[output.length - 1][0].display_inline !==
            this.documentSchema.variable_tabs[t].display_inline
        ) {
          output.push([this.documentSchema.variable_tabs[t]])
          continue
        }
        output[output.length - 1].push(this.documentSchema.variable_tabs[t])
      }

      return output
    },
    documentVariables () {
      return this.compileVariables().reduce((acc, variable) => {
        acc[variable.document_variable_id] = variable
        return acc
      }, {})
    },
    salesRedressSchemes () {
      return this.redressSchemes.filter((rs) => rs.sales)
    },
    lettingsRedressSchemes () {
      return this.redressSchemes.filter((rs) => rs.lettings)
    },
    isDisabled () {
      if (!this.documentSchema) {
        return false
      }

      return !this.isMaster && (
        (this.documentSchema.requires_client_money_protection_provider && !this.selectedOffice.client_money_protection_provider_id) ||
        (this.documentSchema.requires_sales_redress_scheme && !this.selectedOffice.sales_redress_scheme_id) ||
        (this.documentSchema.requires_lettings_redress_scheme && !this.selectedOffice.lettings_redress_scheme_id) ||
        (this.documentSchema.requires_general_compliance_info && (
          !this.selectedOffice.compliance_reg_number ||
          !this.selectedOffice.compliance_reg_name ||
          !this.selectedOffice.compliance_reg_address
        ))
      )
    },
    missingRequiredCount () {
      let count = 0
      for (let t = 0; t < this.documentSchema.variable_tabs.length; t++) {
        for (let g = 0; g < this.documentSchema.variable_tabs[t].groups.length; g++) {
          for (let v = 0; v < this.documentSchema.variable_tabs[t].groups[g].variables.length; v++) {
            const va = this.documentSchema.variable_tabs[t].groups[g].variables[v]
            if (va.type_id === 8) {
              continue
            }
            if (va.required_when_variable_id) {
              const relatedVar = this.getVariableById(va.required_when_variable_id)
              const relatedValue = relatedVar.default_value
              if ([1, '1', true, 'true'].includes(relatedValue)) {
                continue
              }
            }
            if (this.documentSchema.variable_tabs[t].groups[g].is_collapsed) {
              continue
            }
            if (va.required && (!this.isMaster || !va.hide_from_master) && (!va.default_value || ([5, 6].includes(va.type_id) && this.validateCurrency(va.default_label, va.default_value)))) {
              count++
            }
          }
        }
      }

      return count
    }
  },
  mounted () {
    this.getDocumentList()
    this.updateAgentInfo()
  },
  methods: {
    ...mapActions(['displayToastNotification']),
    ...mapMutations(['setSelectedOffice']),
    getDocumentList () {
      this.selectedDocument = null
      this.documentSchema = null

      v2Api
        .get('documents', {
          params: {
            office_id: this.selectedOffice.id,
            list: true
          }
        })
        .then(response => {
          this.documentList = response.data
          if (this.documentList.length > 0) {
            this.selectedDocument = this.documentList[0].id
          }
        })
    },
    getDocumentSchema () {
      if (this.selectedDocument === null) {
        return
      }
      this.documentSchema = null

      v2Api
        .get(`documents/${this.selectedDocument}`, {
          params: {
            office_id: this.selectedOffice.id
          }
        })
        .then(response => {
          this.documentSchema = response.data
          this.selectedTabs = []
          this.selectedTemplate = this.documentSchema.templates[0].id
          for (let i = 0; i < this.sections.length; i++) {
            this.selectedTabs.push(0)
          }
          this.getMasterVariables()

          if (this.isMaster) {
            if (this.documentSchema.requires_sales_redress_scheme || this.documentSchema.requires_lettings_redress_scheme) {
              this.getRedressSchemes()
            }
            if (this.documentSchema.requires_client_money_protection_provider) {
              this.getClientMoneyProtectionProviders()
            }
          }
          if (this.documentSchema.customisable_author) {
            this.getAgentList()
          }
        })
    },
    getMasterVariables () {
      const path = this.savedDocId ? `saved-documents/${this.savedDocId}` : `documents/${this.selectedDocument}/master-variables`
      v2Api.get(path, {
        params: {
          office_id: this.selectedOffice.id
        }
      }).then((response) => {
        this.complianceInfo = response.data.office
        const vars = response.data.variables
        this.masterCopyInfo = response.data.masterCopy
        this.savedDocData.name = this.savedDocId ? response.data.name : this.getDownloadFilename().replace(/\.pdf$/i, '')
        for (let t = 0; t < this.documentSchema.variable_tabs.length; t++) {
          for (let g = 0; g < this.documentSchema.variable_tabs[t].groups.length; g++) {
            for (let v = 0; v < this.documentSchema.variable_tabs[t].groups[g].variables.length; v++) {
              const master = vars.find((master) => master.document_variable_id === this.documentSchema.variable_tabs[t].groups[g].variables[v].id)
              if (!master) {
                continue
              }
              this.documentSchema.variable_tabs[t].groups[g].variables[v].default_value = master.value
              if (this.documentSchema.variable_tabs[t].groups[g].variables[v].customisable_label) {
                this.documentSchema.variable_tabs[t].groups[g].variables[v].default_label = master.label
              }
            }
          }
        }
      })
    },
    generateDocument (saveToWebsite = false) {
      this.generatingDocument = true
      const template = this.documentSchema.templates.find((t) => t.id === this.selectedTemplate)
      this.errorBag.clear()

      v2Api.post(`documents/${this.selectedDocument}/generate`, {
        document_template_id: this.selectedTemplate,
        agent_id: this.agent_id,
        agent_name: this.agent_name,
        agent_position: this.agent_position,
        office_id: this.selectedOffice.id,
        variables: this.compileVariables(),
        save_copy: saveToWebsite
      }, {
        responseType: 'blob'
      }).then((response) => {
        this.generatingDocument = false
        if (saveToWebsite) {
          this.displayToastNotification({
            type: 'success',
            icon: 'fa-check-circle',
            title: 'Saved',
            info: 'The document has been uploaded to the website.'
          })
          this.showSaveToWebsiteModal = false
          return
        }

        let blob = new Blob([response.data])
        if (!window.navigator.msSaveOrOpenBlob) {
          let url = window.URL.createObjectURL(blob)
          let link = document.createElement('a')
          link.href = url
          let downloadFilename = `${this.savedDocData.name}.pdf`
          if (this.documentSchema.output_filename && this.documentVariables) {
            let processedFilename = this.getDownloadFilename()
            downloadFilename = processedFilename.endsWith('.pdf') ? processedFilename : `${processedFilename}.pdf`
          }
          if (this.savedDocId) {
            downloadFilename = `${this.savedDocData.name}.pdf`
          }
          link.setAttribute('download', downloadFilename)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        } else {
          window.navigator.msSaveOrOpenBlob(blob, `${this.documentSchema.name} - ${template.name}.pdf`)
        }
      }, async (errors) => {
        const data = JSON.parse(await errors.response.data.text())
        if (errors.response.status === 422) {
          this.errorBag.store(data)
        }

        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-exclamation-triangle',
          title: 'Error',
          info: data.message
        })
        this.generatingDocument = false
      })
    },
    compileVariables () {
      let data = []
      for (let t = 0; t < this.documentSchema.variable_tabs.length; t++) {
        for (let g = 0; g < this.documentSchema.variable_tabs[t].groups.length; g++) {
          for (let v = 0; v < this.documentSchema.variable_tabs[t].groups[g].variables.length; v++) {
            const va = this.documentSchema.variable_tabs[t].groups[g].variables[v]
            if (va.type_id === 8) {
              continue
            }
            data.push({
              document_variable_id: va.id,
              label: va.customisable_label ? va.default_label : null,
              value: va.default_value,
              value_name: va.value_name
            })
          }
        }
      }

      return data
    },
    saveMasterCopy () {
      this.savingMaster = true
      this.errorBag.clear()

      v2Api.post(`documents/${this.selectedDocument}/master-variables`, {
        office_id: this.selectedOffice.id,
        office: this.complianceInfo,
        variables: this.compileVariables()
      }).then(() => {
        this.savingMaster = false

        this.displayToastNotification({
          type: 'success',
          icon: 'fa-check-circle',
          title: 'Saved',
          info: 'The master copy has been saved.'
        })

        const office = {
          ...this.selectedOffice,
          ...this.complianceInfo
        }
        this.setSelectedOffice(office)
      }, (errors) => {
        if (errors.response.status === 422) {
          this.errorBag.store(errors.response.data)
        }
        this.savingMaster = false

        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-exclamation-triangle',
          title: 'Error',
          info: errors.response.data.message
        })
      })
    },
    validateCurrency (label, val) {
      const acceptsCurrency = (label || '').indexOf('£') >= 0
      const acceptsPercent = (label || '').indexOf('%') >= 0

      if (acceptsCurrency && !acceptsPercent) {
        return /^£[0-9.,]+$/.test(val) ? null : 'Please include the £ symbol in this field.'
      }

      if (!acceptsCurrency && acceptsPercent) {
        return /^[0-9.,]+%$/.test(val) ? null : 'Please include the % symbol in this field.'
      }

      return /^£[0-9.,]+$|^[0-9.,]+%$/.test(val) ? null : 'Please include either the £ or % symbol in this field.'
    },
    validateKeyPress (variable, e) {
      // Currency fields
      if ([5, 6].includes(variable.type_id) && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !/[0-9.,£%]/.test(e.key)) {
        e.preventDefault()
      }
    },
    saveDocument () {
      this.saving = true
      this.errorBag.clear()

      let method = this.savedDocId ? 'patch' : 'post'
      let endpoint = this.savedDocId ? `saved-documents/${this.savedDocId}` : 'saved-documents'
      if (this.savedDocData.save_as_id) {
        method = 'post'
        endpoint = 'saved-documents'
      }
      v2Api[method](endpoint, {
        document_id: this.selectedDocument,
        office_id: this.selectedOffice.id,
        name: this.savedDocData.name,
        variables: this.compileVariables()
      }).then(() => {
        this.saving = false
        this.showSaveModal = false

        this.displayToastNotification({
          type: 'success',
          icon: 'fa-check-circle',
          title: 'Saved',
          info: 'The document has been saved.'
        })
      }, (errors) => {
        if (errors.response.status === 422) {
          this.errorBag.store(errors.response.data)
        }
        this.saving = false

        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-exclamation-triangle',
          title: 'Error',
          info: errors.response.data.message
        })
      })
    },
    getRedressSchemes () {
      v2Api.get('redress-schemes').then((response) => {
        this.redressSchemes = response.data
      })
    },
    getClientMoneyProtectionProviders () {
      v2Api.get('client-money-protection-providers').then((response) => {
        this.cmpProviders = response.data
      })
    },
    getDownloadFilename () {
      let outputFilename = this.documentSchema.output_filename || `${this.documentSchema.name}.pdf`
      const placeholders = outputFilename.match(/\{(.*?)\}/g) || []
      // Convert variables array into an associative object
      const variableMap = {}
      Object.values(this.documentVariables).forEach((variable) => {
        if (!variableMap[variable.value_name] || variableMap[variable.value_name] === null) {
          variableMap[variable.value_name] = variable.value
        }
      })
      // Replace placeholders with matching values
      placeholders.forEach((placeholder) => {
        const key = placeholder.replace(/\{|\}/g, '') // Remove `{}` brackets
        const replacement = variableMap[key] || '' // Replace with value or empty string
        outputFilename = outputFilename.replace(placeholder, replacement)
      })
      return outputFilename
    },
    openSaveModal () {
      this.showSaveModal = true
      if (!this.savedDocId) {
        this.savedDocData.name = this.getDownloadFilename().replace(/\.pdf$/i, '')
      }
    },
    getAgentList () {
      api.get('users', {
        params: {
          office_id: this.selectedOffice.id, // TODO for v2 users
          all: 1
        }
      }).then((response) => {
        this.agents = response.data
      })
    },
    moveGroup (parentIndex, groupIndex, direction) {
      const groups = this.documentSchema.variable_tabs[parentIndex].groups
      if (
        (direction === 'up' && groupIndex === 0) ||
        (direction === 'down' && groupIndex === groups.length - 1)
      ) {
        return // Prevent moving out of bounds
      }
      const newIndex = direction === 'up' ? groupIndex - 1 : groupIndex + 1
      // Swap the groups
      const temp = groups[groupIndex]
      this.$set(groups, groupIndex, groups[newIndex])
      this.$set(groups, newIndex, temp)
    },
    updateAgentInfo () {
      this.agent_name = this.profile.name
      this.agent_position = this.profile.position
      const selectedAgent = this.agents.find(agent => agent.id === this.agent_id)
      if (selectedAgent) {
        this.agent_name = selectedAgent.name
        this.agent_position = selectedAgent.position
      }
    },
    toggleGroup (index) {
      this.$set(this.expandedGroups, index, !this.expandedGroups[index]) // Toggle state
    },
    addTableRow (v) {
      const columns = Array.isArray(v.config.headers) ? v.config.headers.length : 0
      // Initialize v.default_value if null or not an array
      if (!Array.isArray(v.default_value)) {
        v.default_value = []
      }
      // Add a new row
      v.default_value.push(new Array(columns).fill(''))
    },
    removeTableRow (v, rowIndex) {
      if (Array.isArray(v.default_value) && v.default_value.length > 1) {
        v.default_value.splice(rowIndex, 1)
      }
    },
    getVariableById (id) {
      for (let t = 0; t < this.documentSchema.variable_tabs.length; t++) {
        const tab = this.documentSchema.variable_tabs[t]
        for (let g = 0; g < tab.groups.length; g++) {
          const group = tab.groups[g]
          for (let v = 0; v < group.variables.length; v++) {
            const variable = group.variables[v]
            if (variable.id === id) {
              return variable
            }
          }
        }
      }
      return null // not found
    },
    isRequired (va) {
      if (va.required_when_variable_id) {
        const relatedVar = this.getVariableById(va.required_when_variable_id)
        const relatedValue = relatedVar.default_value
        if ([1, '1', true, 'true'].includes(relatedValue)) {
          return false
        }
      }
      if (va.required) {
        return true
      }
      return false
    }
  },
  watch: {
    selectedDocument () {
      this.getDocumentSchema()
    },
    selectedOffice () {
      this.getDocumentList()
    },
    'savedDocData.save_as_id' (newValue) {
      this.savedDocData.name = this.savedDocData.name
      if (newValue === 1) {
        this.savedDocData.name = this.getDownloadFilename().replace(/\.pdf$/i, '')
      } else {
        this.getMasterVariables()
      }
    },
    'agent_id' (newValue) {
      this.updateAgentInfo()
    }
  }
}
</script>
